import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { corporateTheme } from '../themes/corporate';

const PageContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  background: ${corporateTheme.backgroundGradient};
  padding: 120px 2rem 4rem;
  font-family: ${corporateTheme.fontFamily};
`;

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const Title = styled(motion.h1)`
  font-size: clamp(2.5rem, 5vw, 3.5rem);
  font-weight: 700;
  color: ${corporateTheme.textPrimary};
  margin-bottom: 2rem;
  text-align: center;
`;

const LastUpdated = styled.p`
  text-align: center;
  color: ${corporateTheme.textSecondary};
  margin-bottom: 3rem;
  font-style: italic;
`;

const Content = styled(motion.div)`
  background: ${corporateTheme.cardBackground};
  border-radius: 20px;
  padding: 3rem;
  box-shadow: ${corporateTheme.cardShadow};
  border: 1px solid ${corporateTheme.cardBorder};
`;

const Section = styled.section`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  color: ${corporateTheme.primary};
  margin-bottom: 1rem;
  font-weight: 600;
`;

const Text = styled.p`
  color: ${corporateTheme.textSecondary};
  line-height: 1.7;
  margin-bottom: 1rem;
`;

const List = styled.ul`
  color: ${corporateTheme.textSecondary};
  line-height: 1.7;
  margin-left: 1.5rem;
  margin-bottom: 1rem;
`;

const TermsOfService = () => {
  return (
    <PageContainer>
      <Container>
        <Title
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          Terms of Service
        </Title>
        
        <LastUpdated>Last updated: December 2024</LastUpdated>
        
        <Content
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <Section>
            <Text>
              These Terms of Service ("Terms") govern your use of the GrowTech Ltd affiliate marketing platform 
              and services. By accessing or using our services, you agree to be bound by these Terms.
            </Text>
          </Section>

          <Section>
            <SectionTitle>1. Acceptance of Terms</SectionTitle>
            <Text>
              By creating an account or using our platform, you acknowledge that you have read, understood, and agree 
              to be bound by these Terms and our Privacy Policy. If you do not agree to these Terms, you may not use 
              our services.
            </Text>
          </Section>

          <Section>
            <SectionTitle>2. Description of Service</SectionTitle>
            <Text>
              GrowTech Ltd provides an affiliate marketing platform that connects advertisers with affiliates. 
              Our services include:
            </Text>
            <List>
              <li>Affiliate program management and tracking</li>
              <li>Commission processing and payments</li>
              <li>Performance analytics and reporting</li>
              <li>Marketing materials and support</li>
              <li>Fraud detection and prevention</li>
            </List>
          </Section>

          <Section>
            <SectionTitle>3. Account Registration and Eligibility</SectionTitle>
            <Text>
              To use our services, you must:
            </Text>
            <List>
              <li>Be at least 18 years old or the age of majority in your jurisdiction</li>
              <li>Provide accurate and complete registration information</li>
              <li>Maintain the security of your account credentials</li>
              <li>Comply with all applicable laws and regulations</li>
              <li>Not engage in fraudulent or deceptive practices</li>
            </List>
          </Section>

          <Section>
            <SectionTitle>4. Affiliate Responsibilities</SectionTitle>
            <Text>As an affiliate, you agree to:</Text>
            <List>
              <li>Promote advertiser products/services honestly and accurately</li>
              <li>Comply with advertising standards and regulations</li>
              <li>Not engage in spam, fraud, or misleading practices</li>
              <li>Obtain necessary permissions for promotional activities</li>
              <li>Maintain professional standards in all communications</li>
            </List>
          </Section>

          <Section>
            <SectionTitle>5. Advertiser Responsibilities</SectionTitle>
            <Text>As an advertiser, you agree to:</Text>
            <List>
              <li>Provide accurate product/service information</li>
              <li>Honor commission agreements and payment terms</li>
              <li>Comply with applicable laws and industry standards</li>
              <li>Provide timely support for affiliate inquiries</li>
              <li>Maintain quality standards for promoted products/services</li>
            </List>
          </Section>

          <Section>
            <SectionTitle>6. Payments and Commissions</SectionTitle>
            <Text>
              Commission payments are subject to our payment terms and conditions. We reserve the right to 
              withhold payments for fraudulent activity, chargebacks, or violations of these Terms. 
              Payment schedules and minimum thresholds are specified in your affiliate agreement.
            </Text>
          </Section>

          <Section>
            <SectionTitle>7. Prohibited Activities</SectionTitle>
            <Text>You may not:</Text>
            <List>
              <li>Engage in fraudulent clicks, conversions, or traffic</li>
              <li>Use automated systems to generate false activity</li>
              <li>Violate any applicable laws or regulations</li>
              <li>Infringe on intellectual property rights</li>
              <li>Interfere with platform security or functionality</li>
              <li>Create multiple accounts without authorization</li>
            </List>
          </Section>

          <Section>
            <SectionTitle>8. Termination</SectionTitle>
            <Text>
              Either party may terminate this agreement at any time with notice. We may suspend or terminate 
              accounts immediately for violations of these Terms. Upon termination, you remain liable for 
              any outstanding obligations.
            </Text>
          </Section>

          <Section>
            <SectionTitle>9. Limitation of Liability</SectionTitle>
            <Text>
              GrowTech Ltd's liability is limited to the maximum extent permitted by law. We are not liable 
              for indirect, incidental, or consequential damages arising from your use of our services.
            </Text>
          </Section>

          <Section>
            <SectionTitle>10. Governing Law</SectionTitle>
            <Text>
              These Terms are governed by the laws of Hong Kong. Any disputes will be resolved through 
              binding arbitration in Hong Kong.
            </Text>
          </Section>

          <Section>
            <SectionTitle>11. Changes to Terms</SectionTitle>
            <Text>
              We may modify these Terms at any time. We will notify users of material changes. 
              Continued use of our services after changes constitutes acceptance of the new Terms.
            </Text>
          </Section>

          <Section>
            <SectionTitle>12. Contact Information</SectionTitle>
            <Text>
              For questions about these Terms, please contact us at:
              <br /><br />
              <strong>GrowTech Ltd</strong><br />
              Email: info@growtech.cc<br />
              Address: Hong Kong
            </Text>
          </Section>
        </Content>
      </Container>
    </PageContainer>
  );
};

export default TermsOfService; 